import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

function DatePicker({ 
  value, 
  onChange, 
  placeholder = "DD/MM/YYYY",
  className,
  disabled = false,
  ...props 
}) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  
  // Parse the string date value (YYYY-MM-DD) to Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    try {
      const parsed = parse(value, 'yyyy-MM-dd', new Date())
      return isValid(parsed) ? parsed : undefined
    } catch {
      return undefined
    }
  }, [value])

  // Update input display when value changes
  React.useEffect(() => {
    if (dateValue) {
      setInputValue(format(dateValue, 'dd/MM/yyyy'))
    } else if (!value) {
      setInputValue('')
    }
  }, [dateValue, value])

  // Handle calendar date selection
  const handleCalendarSelect = (date) => {
    if (date) {
      const formatted = format(date, 'yyyy-MM-dd')
      onChange(formatted)
      setInputValue(format(date, 'dd/MM/yyyy'))
    }
    setOpen(false)
  }

  // Handle manual input
  const handleInputChange = (e) => {
    const val = e.target.value
    setInputValue(val)
    
    // Try to parse as DD/MM/YYYY
    if (val.length === 10) {
      try {
        const parsed = parse(val, 'dd/MM/yyyy', new Date())
        if (isValid(parsed)) {
          onChange(format(parsed, 'yyyy-MM-dd'))
        }
      } catch {
        // Invalid date, don't update
      }
    }
  }

  // Handle blur - validate and format
  const handleInputBlur = () => {
    if (!inputValue) {
      onChange('')
      return
    }
    
    // Try multiple formats
    const formats = ['dd/MM/yyyy', 'dd-MM-yyyy', 'd/M/yyyy', 'd-M-yyyy', 'ddMMyyyy']
    for (const fmt of formats) {
      try {
        const parsed = parse(inputValue, fmt, new Date())
        if (isValid(parsed)) {
          onChange(format(parsed, 'yyyy-MM-dd'))
          setInputValue(format(parsed, 'dd/MM/yyyy'))
          return
        }
      } catch {
        continue
      }
    }
    
    // If no valid date, reset to previous value
    if (dateValue) {
      setInputValue(format(dateValue, 'dd/MM/yyyy'))
    } else {
      setInputValue('')
    }
  }

  return (
    <div className={cn("flex gap-1", className)}>
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1"
        {...props}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            disabled={disabled}
            className="shrink-0"
            type="button"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={dateValue}
            onSelect={handleCalendarSelect}
            initialFocus
            captionLayout="dropdown-buttons"
            fromYear={1940}
            toYear={2040}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

DatePicker.displayName = "DatePicker"

export { DatePicker }
